import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import gsap from 'gsap';
import { SplitText } from 'gsap/SplitText';
import './HeroSlider.css';
import heroBg from '../../assets/1d511ceb-a632-4d1d-8887-2b8c7a6994c3.jpg';

gsap.registerPlugin(SplitText);

// Detect mobile/touch device
const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    || window.innerWidth <= 1024;
};

const HeroSlider = () => {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const titleRef = useRef(null);
  const splitRef = useRef(null);
  const cursorRef = useRef(null);
  const [isHovering, setIsHovering] = useState(false);
  const [isInteractive, setIsInteractive] = useState(false);
  const isMobile = isMobileDevice();

  useEffect(() => {
    // Skip all mouse/cursor effects on mobile/touch
    if (isMobile) return;

    // Throttle mousemove with requestAnimationFrame for 60fps smooth
    let rafId = null;
    const handleMagnetic = (e) => {
      const btn = e.currentTarget;
      const rect = btn.getBoundingClientRect();
      const x = e.clientX - rect.left - rect.width / 2;
      const y = e.clientY - rect.top - rect.height / 2;
      gsap.to(btn, { x: x * 0.4, y: y * 0.4, duration: 0.3, ease: 'power2.out' });
    };

    const resetMagnetic = (e) => {
      gsap.to(e.currentTarget, { x: 0, y: 0, duration: 0.5, ease: 'elastic.out(1, 0.3)' });
    };

    const handleMouseMove = (e) => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        if (!containerRef.current) return;

        if (cursorRef.current) {
          gsap.to(cursorRef.current, {
            x: e.clientX,
            y: e.clientY,
            duration: 0.15,
            ease: "power2.out"
          });
        }

        if (window.innerWidth > 1400) {
          const tiltX = (e.clientY / window.innerHeight - 0.5) * 8;
          const tiltY = (e.clientX / window.innerWidth - 0.5) * -8;
          gsap.to(".text", {
            rotateX: tiltX,
            rotateY: tiltY,
            duration: 1.2,
            ease: "power2.out",
            transformPerspective: 1000
          });
        }

        const target = e.target;
        const interactive = target.closest('.btn') || target.closest('a');
        setIsInteractive(!!interactive);
      });
    };

    const handleMouseEnter = () => setIsHovering(true);
    const handleMouseLeave = () => setIsHovering(false);

    const ctx = gsap.context(() => {
      const split = new SplitText(titleRef.current, { type: "chars", charsClass: "brand-char-hero" });
      const tl = gsap.timeline();

      tl.fromTo(containerRef.current,
        { opacity: 0 },
        { opacity: 1, duration: 1.5, ease: 'power2.out' }
      );

      tl.fromTo(split.chars,
        { y: "100%", opacity: 0 },
        {
          y: "0%",
          opacity: 1,
          duration: 1,
          stagger: 0.08,
          ease: "power4.out",
          onComplete: () => split.revert()
        },
        "-=1"
      );

      tl.fromTo(".text-section h4",
        { x: -20, opacity: 0, letterSpacing: '20px' },
        { x: 0, opacity: 0.7, letterSpacing: '10px', duration: 1.2, ease: 'power3.out' },
        "-=0.8"
      );

      tl.fromTo(".script-tagline",
        { y: 30, opacity: 0 },
        { y: 0, opacity: 0.9, duration: 1, ease: 'power2.out' },
        "-=0.6"
      );

      tl.fromTo(".buttons .btn",
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8, stagger: 0.2, ease: 'back.out(1.7)' },
        "-=0.5"
      );
    }, containerRef);

    const container = containerRef.current;
    if (container) {
      container.addEventListener("mousemove", handleMouseMove);
      container.addEventListener("mouseenter", handleMouseEnter);
      container.addEventListener("mouseleave", handleMouseLeave);
    }

    const buttons = document.querySelectorAll('.hero .btn');
    buttons.forEach(btn => {
      btn.addEventListener('mousemove', handleMagnetic);
      btn.addEventListener('mouseleave', resetMagnetic);
    });

    return () => {
      ctx.revert();
      if (rafId) cancelAnimationFrame(rafId);
      if (container) {
        container.removeEventListener("mousemove", handleMouseMove);
        container.removeEventListener("mouseenter", handleMouseEnter);
        container.removeEventListener("mouseleave", handleMouseLeave);
      }
      buttons.forEach(btn => {
        btn.removeEventListener('mousemove', handleMagnetic);
        btn.removeEventListener('mouseleave', resetMagnetic);
      });
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTitleHover = () => {
    if (!titleRef.current || splitRef.current) return;

    // Create new split for hover animation
    splitRef.current = new SplitText(titleRef.current, {
      type: "chars",
      charsClass: "brand-char-hero"
    });

    gsap.fromTo(splitRef.current.chars,
      { y: "100%", opacity: 0 },
      {
        y: "0%",
        opacity: 1,
        duration: 0.7,
        stagger: 0.04,
        ease: "power4.out",
        onComplete: () => {
          if (splitRef.current) {
            splitRef.current.revert();
            splitRef.current = null;
          }
        }
      }
    );
  };

  const handleLabelMouseEnter = (e) => {
    gsap.to(e.currentTarget.querySelector('.discovery-label-inner'), {
      rotationX: 180,
      duration: 0.6,
      ease: "power2.inOut"
    });
  };

  const handleLabelMouseLeave = (e) => {
    gsap.to(e.currentTarget.querySelector('.discovery-label-inner'), {
      rotationX: 0,
      duration: 0.6,
      ease: "power2.inOut"
    });
  };

  return (
    <section className="hero" ref={containerRef} style={{ cursor: 'pointer' }}>

      {/* 0. Custom Liquid Cursor */}
      <div
        ref={cursorRef}
        className={`hero-custom-cursor ${isHovering ? 'visible' : ''} ${isInteractive ? 'active' : ''}`}
      >
        <div className="cursor-dot" />
        <div className="cursor-ring" />
        <span className="cursor-text">{isInteractive ? 'DISCOVER' : 'EXPLORE'}</span>
      </div>
      {/* Background Image Layer */}
      <div className="hero-video-container">
        <img
          src={heroBg}
          alt="Escentrum Luxury Perfume"
          className="bg-video-main"
        />
        <div className="video-overlay"></div>
        <div className="grain-overlay"></div>
        {/* Smoke / Smog Wave Layers */}
        <div className="smoke-wave smoke-wave-1"></div>
        <div className="smoke-wave smoke-wave-2"></div>
        <div className="smoke-wave smoke-wave-3"></div>
      </div>

      <div className="hero-content centered">
        {/* 1. MODERN FLOATING PARTICLES */}
        <div className="hero-particles">
          {[...Array(6)].map((_, i) => (
            <div key={i} className={`particle p-${i}`} />
          ))}
        </div>

        <div className="text-section">
          <div
            className="discovery-label-container"
            onMouseEnter={handleLabelMouseEnter}
            onMouseLeave={handleLabelMouseLeave}
          >
            <div className="discovery-label-inner">
              <h4 className="discovery-label-front">DISCOVERY SET</h4>
              <h4 className="discovery-label-back">EXPLORE NOW</h4>
            </div>
          </div>
          <h1
            className="hero-title hero-title-reveal"
            ref={titleRef}
            onMouseEnter={handleTitleHover}
          >
            ESCENTRUM
          </h1>
          <p className="script-tagline">Crafting the Future of Fragrance</p>
          <div className="buttons">
            <button 
              type="button"
              onClick={() => navigate('/collections')} 
              className="btn primary" 
              style={{ cursor: 'pointer', border: 'none' }}
            >
              EXPLORE COLLECTION
            </button>
            <button 
              type="button"
              onClick={() => navigate('/shop')} 
              className="btn outline" 
              style={{ cursor: 'pointer' }}
            >
              NEW ARRIVALS
            </button>
          </div>
        </div>

        {/* 3. MODERN SCROLL INDICATOR */}
        <div className="modern-scroll-hint">
          <div className="scroll-line">
            <div className="scroll-progress" />
          </div>
          
        </div>
       
        {/* Removed heavy SVG liquid filter — causes GPU jank on mobile */}
      </div>
    </section>
  );
};
export default HeroSlider;
